'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { COLORS } from '@/constants';
import lodashIsEqual from 'lodash/isEqual';
import { StyledButton } from '@/features/pages/home/components/Hero/styled';
import { WalletIcon } from '@/components/icons';

export interface IconToggleOption<T> {
  value: T;
  title?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  count?: number;
}

interface IconToggleGroupProps<T> {
  options: IconToggleOption<T>[];
  selected?: T[];
  onChange: (value: T[]) => void;
  disabled?: boolean;
  defaultIcon?: React.ReactNode;
  hideDefaultIcon?: boolean;
  isEqual?: (a: T, b: T) => boolean;
  sx?: React.ComponentProps<typeof Box>['sx'];
  equalWidth?: boolean;
  multiple?: boolean;
  label?: string;
}

export function IconToggleGroup<T>({
  options,
  selected = [],
  onChange,
  disabled = false,
  defaultIcon,
  hideDefaultIcon = false,
  isEqual = lodashIsEqual,
  sx,
  equalWidth = false,
  label,
  multiple = false,
}: IconToggleGroupProps<T>) {
  const handleChange = (value: T) => {
    if (disabled) return;

    if (multiple) {
      const newSelected = selected.some((item) => isEqual(item, value))
        ? selected.filter((item) => !isEqual(item, value))
        : [...selected, value];
      onChange(newSelected);
    } else {
      onChange(selected.some((item) => isEqual(item, value)) ? [] : [value]);
    }
  };

  const isActive = (value: T) => {
    return selected.some((item) => isEqual(item, value));
  };

  return (
    <Box display="flex" flexDirection={'column'} sx={sx}>
      {label && (
        <Typography fontWeight={700} fontSize={16} lineHeight="125%" mb={1}>
          {label}
        </Typography>
      )}
      <Box
        display={equalWidth ? 'grid' : 'flex'}
        gap={1.5}
        flexWrap={equalWidth ? undefined : 'wrap'}
        width="100%"
        sx={
          equalWidth
            ? {
                gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
                ...sx,
              }
            : sx
        }
      >
        {options.map((option, index) => {
          const active = isActive(option.value);
          const isOptionDisabled = disabled || option.disabled;
          const label = option?.title || '';

          return (
            <StyledButton
              key={index}
              variant="outlined"
              disabled={isOptionDisabled}
              onClick={() => handleChange(option.value)}
              $isActive={active}
              startIcon={
                !hideDefaultIcon
                  ? (option.icon ??
                    defaultIcon ?? <WalletIcon fill={active ? COLORS.orange : COLORS.gray} />)
                  : option.icon
              }
              sx={
                equalWidth
                  ? {
                      height: 'auto !important',
                      minHeight: '40px',
                      padding: '8px 12px !important',
                      minWidth: '0 !important',
                      width: '100% !important',
                      flex: '1 1 0',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  : {
                      height: '34px !important',
                      padding: '10px 8px !important',
                      minWidth: 'auto',
                      flex: '0 0 auto',
                    }
              }
            >
              {label && (
                <Typography
                  textTransform="none"
                  fontSize={equalWidth ? 15 : 18}
                  lineHeight={equalWidth ? 1.25 : 1}
                  fontWeight={600}
                  sx={
                    equalWidth
                      ? { textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }
                      : undefined
                  }
                  color={active ? COLORS.orange : COLORS.gray}
                >
                  {option?.count ? `${label} (${option.count})` : label}
                </Typography>
              )}
            </StyledButton>
          );
        })}
      </Box>
    </Box>
  );
}
